import sanitizeBranchName from 'branch-safe-name/sanitize';

import {
  type ReproduceRun_FileFragment as FileInfo,
  type ReproduceRun_RunFragment as RunInfo,
} from '../../generated/graphql';
import * as Urls from '../../util/urls';
import {useGetFileDownloadUrl} from './useGetFileDownloadUrl';
import {findPatchFile} from './utils';

/**
 * Wraps a shell argument in double quotes if it contains characters that the
 * shell would interpret: whitespace (which causes token splitting), pipes,
 * redirects, and background operators. Handles the `--flag=value` form by
 * quoting only the value portion, e.g. `--output=some dir` → `--output="some dir"`.
 */
export function quoteArgument(a: string): string {
  if (a.match(/[\s|&<>]/g)) {
    let prefix = '';
    // Check if the arg is like --foo=bla bla
    if (a.match(/^[\w-]+=/)) {
      const parts = a.split('=');
      prefix = parts.shift() + '=';
      a = parts.join('=');
    }
    return `${prefix}"${a.replace(/"/g, '\\"')}"`;
  }
  return a;
}

export function makeCloneCommand(remote: string): string {
  return `git clone ${remote}`;
}

export function makeCheckoutCommand(
  commit: string,
  displayName: string | null
) {
  return `git checkout -b "${sanitizeBranchName(displayName ?? 'reproduce_run', '-')}" ${commit}`;
}

// Generate an array of steps the user can perform to reproduce the run in
// question. Some of the steps are best-guess.
function makeSetupSteps(
  remote: string | null,
  commit: string | null,
  patchDownloadUrl: string | null,
  displayName: string | null
): string[] | null {
  const lines: string[] = [];

  // If this run has no remote, we can't reproduce it. Sorry :(
  if (!remote || !commit) {
    return null;
  }

  // Clone the remote, then cd into that directory. `$_` returns the last arg
  // of the previous command (so the value of 'remote'), and `basename` returns
  // the last part of a path (so the name of the repo). We also strip off the
  // .git suffix, because the directory we are creating will be named after the
  // repo name, excluding the .git part.
  const cloneLine = makeCloneCommand(remote);
  const cdLine = `cd "$(basename "$_" .git)"`;
  lines.push(cloneLine, cdLine);

  const checkoutLine = makeCheckoutCommand(commit, displayName);
  lines.push(checkoutLine);

  // If there's a patch, add commands for patching the directory using the patch
  // file. The `patchDownloadUrl` is expected to be quite large.
  if (patchDownloadUrl) {
    const patchLine = `curl "${patchDownloadUrl}" > diff.patch`;
    const applyLine = `git apply diff.patch`;
    lines.push(patchLine, applyLine);
  }

  return lines;
}

export function makeExecuteCommand(
  program: string,
  args: string[] | null
): string {
  const isNotebook = program.includes('.ipynb');
  const argString =
    args && !isNotebook ? ' ' + args.map(quoteArgument).join(' ') : '';
  return `${program}${argString}`;
}

type UseRunReproduceStepsHook = {
  commit: string | null;
  executable: string | null;
  executeSteps: string[] | null;
  patch: FileInfo | null;
  patchViewUrl: string | null;
  remote: string | null;
  setupSteps: string[] | null;
};

export function useRunReproduceSteps(run: RunInfo): UseRunReproduceStepsHook {
  const patchFile = findPatchFile(run.reproduceRunFiles);
  const remote = run.runInfo?.git?.remote ?? null;
  const commit = run.runInfo?.git?.commit ?? null;
  const program = run.runInfo?.program ?? null;
  const entityName = run.project?.entityName;
  const projectName = run.project?.name;
  const displayName = run.displayName ?? null;
  const executable = run.runInfo?.executable ?? null;
  const runName = run.name;
  const args = run.runInfo?.args ?? null;
  const fileName = patchFile?.name;

  const patchDownloadUrl = useGetFileDownloadUrl({
    entityName,
    projectName,
    runName,
    fileName: patchFile?.name,
  });

  const patchViewUrl =
    entityName && projectName && runName && fileName
      ? Urls.runTabFile(entityName, projectName, runName, [fileName])
      : null;

  const setupSteps = makeSetupSteps(
    remote,
    commit,
    patchDownloadUrl,
    displayName
  );

  let executeSteps;
  if (program) {
    executeSteps = [makeExecuteCommand(program, args)];
  } else {
    executeSteps = null;
  }

  return {
    commit,
    executeSteps,
    executable,
    patch: patchFile,
    patchViewUrl,
    remote,
    setupSteps,
  };
}
